/**
 * Sortable wrapper for a Kanban card.
 *
 * Layout rule (dnd-kit + custom faded placeholder):
 * - DragOverlay = solid floating card
 * - Own slot targeted: this card stays in place, faded (Trello), so dnd-kit
 *   measures a real card-sized rect at drag start
 * - Other slot targeted: this card collapses and KanbanFadedCardPlaceholder
 *   reserves the insert space
 * - Do NOT apply sortable transforms here — stacking both caused overlapping
 *   ghosts and an extra empty gap (common dnd-kit pitfall with custom placeholders)
 * - Neighbor sliding is FLIP on `data-kanban-flip` (useKanbanFlip), not dnd-kit
 */

import { useSortable } from '@dnd-kit/sortable';
import { cn } from '@/utils/cn';
import { getKanbanSortableItemClass } from './Kanban.styles';
import type { KanbanSortableItemProps } from './Kanban.types';

export function KanbanSortableItem({
  id,
  children,
  disabled = false,
  data,
  isDragSourceCollapsed = false,
  flipFrom,
  className,
}: KanbanSortableItemProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id,
    data: { type: 'card' as const, ...data },
    disabled,
    animateLayoutChanges: () => false,
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        getKanbanSortableItemClass(isDragging, isDragSourceCollapsed),
        className
      )}
      data-kanban-flip={id}
      data-kanban-flip-from={flipFrom}
      {...attributes}
      {...listeners}
      aria-roledescription="draggable card"
    >
      {children}
    </div>
  );
}
